import { useCallback, useEffect, useState } from "react";
import { Title } from '../../utils/title';
import { useParams } from "react-router";
import { OwnerNavBar } from "./NavBar";
import { fetchApi } from "../../services/api";
import { ILink } from '../../types/link';
import { Button, ButtonGroup } from "@progress/kendo-react-buttons";
import { IDNameViewModel } from "TypeGen/id-name-view-model";
import { GridLoadingPanel } from "components/GridLoadingPanel";
import { Grid, GridColumn as Column, GridToolbar } from "@progress/kendo-react-grid";
import DateCell from "components/cells/DateCell";
import DateOnlyCell from "components/cells/DateOnlyCell";
import { arrowRotateCwIcon, plusIcon } from "@progress/kendo-svg-icons";
import NoteEditUI from "./NoteEditUI";
import dayjs from 'dayjs';

type RouteComponentParams = {
  ownerId: string;
};

type Response = {
  Notes: OwnerPerformanceNote[],
  NoteTypes: IDNameViewModel[],
  Links: ILink[];
}

type OwnerPerformanceNote = {
  OwnerNoteID: number;
  ModifiedByFullName: string;
  ModifiedDateTime: Date;
  Note: string;
  OccurredDate: string;
  NoteTypeID: number;
  NoteTypeName: string;
}

export type NewOwnerPerformanceNote = {
  OwnerNoteID: number;
  Note: string;
  OccurredDate: Date | null;
  NoteType: number;
}

const OwnerPerformance = () => {

  const { ownerId: ownerIdParam } = useParams<RouteComponentParams>();
  const [loading, setLoading] = useState(true);
  const [notes, setNotes] = useState<OwnerPerformanceNote[]>([]);
  const [noteTypes, setNoteTypes] = useState<IDNameViewModel[]>([]);
  const [filterNoteTypes, setFilterNoteTypes] = useState<number[]>([]);
  const [note, setNote] = useState<NewOwnerPerformanceNote>();

  const refresh = useCallback(() => {
    setLoading(true);
    fetchApi(`/api/Asset/OwnerPerformance/${ownerIdParam}`)
      .then((data: Response) => {
        setLoading(false);
        setNoteTypes(data.NoteTypes);
        setNotes(data.Notes);
      })
      .catch(err => {
        alert(err);
        setLoading(false);
      });
  }, [ownerIdParam]);

  useEffect(() => {
    refresh();
  }, [ownerIdParam, refresh]);

  const addNote = (newNote: NewOwnerPerformanceNote) => {
    setLoading(true);
    fetchApi(`/api/Asset/AddOwnerNote/${ownerIdParam}`, newNote, 'POST')
      .then((response: { OwnerNoteID: number }) => {
        setNote({
          ...newNote,
          OwnerNoteID: response.OwnerNoteID
        });
        refresh();
      })
      .catch((e) => {
        // If not problem details
        if (!e?.status) alert('An error occurred while saving the note.');
      })
      .finally(() => {
        setLoading(false);
      });
  }

  return (<>
    <OwnerNavBar id={ownerIdParam} currentTab="Performance" />
    <Title string="Performance" />
    <br />
    {note && <NoteEditUI
      loading={loading}
      performanceNote={note}
      noteTypes={noteTypes}
      cancel={() => setNote(undefined)}
      save={addNote}
    />}
    {loading && <GridLoadingPanel />}
    <Grid
      data={notes.filter((note) => filterNoteTypes.length === 0 || filterNoteTypes.includes(note.NoteTypeID))}
      dataItemKey="OwnerNoteID"
      onRowClick={(e) => setNote({
        OwnerNoteID: e.dataItem.OwnerNoteID,
        Note: e.dataItem.Note,
        OccurredDate: e.dataItem.OccurredDate ? dayjs.utc(e.dataItem.OccurredDate).toDate() : null,
        NoteType: e.dataItem.NoteTypeID
      })}
    >
      <GridToolbar>
        <Button onClick={refresh} icon="refresh" svgIcon={arrowRotateCwIcon} title="Refresh" />
        <Button
          icon="plus"
          svgIcon={plusIcon}
          themeColor="primary"
          disabled={loading}
          onClick={() => setNote({
            OwnerNoteID: 0,
            Note: '',
            OccurredDate: null,
            NoteType: 1
          })}
        > Performance Note
        </Button>
        <ButtonGroup>
          {noteTypes.map((noteType) => <Button
            key={noteType.ID}
            togglable={true}
            selected={filterNoteTypes.includes(noteType.ID)}
            onClick={() => {
              if (filterNoteTypes.includes(noteType.ID)) {
                setFilterNoteTypes(filterNoteTypes.filter((id) => id !== noteType.ID));
              } else {
                setFilterNoteTypes([...filterNoteTypes, noteType.ID]);
              }
            }}
          >
            {noteType.Name}
          </Button>)}
        </ButtonGroup>
      </GridToolbar>
      <Column field="Note" title="Note" width={window.innerWidth / 3} />
      <Column field="NoteTypeName" title="Type" />
      <Column field="OccurredDate" cells={{ data: DateOnlyCell }} title="Occurred" />
      <Column field="ModifiedDateTime" cells={{ data: DateCell }} title="Entered" />
      <Column field="ModifiedByFullName" title="Entered By" />
    </Grid>
  </>);
}

export default OwnerPerformance;
